import { useTranslation } from 'react-i18next';
import './Billing.css';
import { useBilling } from './useBilling';
import React, { useContext, useMemo } from 'react';
import { EmptyState, SubscriptionUsage } from '@anticatrade/shared';
import { Spinner, useDeferredLoading } from '@anticatrade/shared';
import { AppContext, appContext } from '../../../context/AppContext';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import { Heading } from '../../base/Heading/Heading';

const Billing = () => {
	const { t, i18n } = useTranslation();
	const { merchant, handleError } = useContext(appContext as React.Context<AppContext>);
	const { isLoading, subscriptionUsage, merchantOrders, showInvoice } = useBilling();

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const { percentUsed, remaining, isExpired } = useMemo(() => {
		if (isLoading) {
			return {
				percentUsed: 0,
				remaining: 0,
				isExpired: false,
			};
		}
		const u = subscriptionUsage as SubscriptionUsage;
		const percentUsed = ((u.used / u.limit) * 100).toFixed(2);
		const remaining = Math.max(u.limit - u.used, 0);
		const isExpired = new Date(merchant!.subscriptionEnd!) < new Date();
		return {
			percentUsed,
			remaining,
			isExpired,
		};
	}, [subscriptionUsage]);

	const onShowInvoice = async (id: number) => {
		try {
			showInvoice(id);
		} catch (err) {
			handleError(err as Error);
			return;
		}
	};

	return (
		<div className='billing'>
			{showContent ? (
				<div className='billing__content'>
					<Heading left={<h1 className='billing__title'>{t('Billing')}</h1>} />
					<div className='billing__subscription wrapped'>
						<div className='billing__subscription-title'>{t('Your subscription')}</div>
						<div className='billing__subscription-type'>
							{t('Annual plan of {count} objects').replace(
								'{count}',
								String(subscriptionUsage!.limit),
							)}
						</div>
						<div
							className={`billing__subscription-end${
								isExpired ? ' billing__subscription-end--danger' : ''
							}`}
						>
							{isExpired ? t('Subscription ended on') : t('Subscription ends on')}{' '}
							{new Date(merchant!.subscriptionEnd!).toLocaleDateString(i18n.language)}
						</div>
						<div className='billing__subscription-usage'>
							{t('Uploaded items')}:{' '}
							<span>
								<span className='billing__subscription-usage-used'>
									{subscriptionUsage?.used}
								</span>
								/
								<span className='billing__subscription-usage-limit'>
									{subscriptionUsage?.limit}
								</span>
								<span className='billing__subscription-usage-percent'>
									{' '}
									({percentUsed}%)
								</span>
							</span>
						</div>
						{!isExpired && (
							<div
								className={`billing__subscription-remaining${
									remaining < 20 ? ' billing__subscription-remaining--danger' : ''
								}`}
							>
								{remaining === 0
									? t('You have reached your subscription limit')
									: t('You have {count} items left to upload').replace(
											'{count}',
											String(remaining),
										)}
							</div>
						)}
					</div>
					<div className='billing__merchant-orders wrapped'>
						<div className='billing__merchant-orders-title'>{t('Your invoices')}</div>
						{merchantOrders!.length > 0 ? (
							<div>
								{' '}
								<div className='billing__merchant-orders-head'>
									<div>{t('ID')}</div>
									<div>{t('Issuance/renewal')}</div>
									<div>{t('Description')}</div>
									<div>{t('Amount')}</div>
								</div>
								{merchantOrders!.map((o) => {
									return (
										<div className='billing__merchant-orders-order'>
											<div className='billing__merchant-orders-id'>
												<span className='billing__merchant-orders-id-label'>
													{t('ID')}:{' '}
												</span>
												{o.invoiceID ? '#' + o.invoiceID : '-'}
											</div>
											<div className='billing__merchant-orders-dates'>
												<div className='billing__merchant-orders-date'>
													{t('Issued on')}{' '}
													{new Date(o.date).toLocaleDateString()}
												</div>
												{o.renewalDate != null && (
													<div className='billing__merchant-orders-renewal-date'>
														{t('To be renewed on')}{' '}
														{new Date(
															o.renewalDate,
														).toLocaleDateString()}
													</div>
												)}
											</div>
											<div className='billing__merchant-orders-description'>
												{o.description}
											</div>
											<div className='billing__merchant-orders-amount'>
												<span className='billing__merchant-orders-amount-label'>
													{t('Amount')}:{' '}
												</span>
												{o.amount == null ? 0 : o.amount}€
											</div>
											<div className='billing__merchant-orders-buttons'>
												{o.amount != null && (
													<SlButton
														variant='text'
														onClick={() => onShowInvoice(o.id)}
													>
														{t('View invoice')}
													</SlButton>
												)}
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<EmptyState
								title={t('Your invoices title')}
								description={t('Your invoices description')}
								icon='file-text'
							/>
						)}
					</div>
				</div>
			) : (
				showSpinner && <Spinner />
			)}
		</div>
	);
};

export { Billing };
